<template>
	<v-card
		:width="displayResponsive ? '100%' : sidebarWidth + 'px'"
		:height="displayResponsive ? 'calc(100svh - 55px)' : '100vh'"
		style="top: 0; right: 0; background-color: rgb(var(--v-theme-contained))"
		:style="displayResponsive ? '' : 'position: absolute;'"
		density="compact"
		class="questions-sidebar-card"
	>
		<div v-if="!displayResponsive" id="questionsborder"></div>
		<template #text>
			<div
				style="display: flex; flex-direction: column"
				:style="displayResponsive ? 'height: calc(100svh - 55px - 42px);' : 'height: calc(100vh - 42px);'"
			>
				<div>
					<v-btn v-if="betaCaseId" block color="success" class="mb-2" @click="clickFinishBeta">
						{{ symbolsStore.GUILabel("b5b589b5-47a6-4d47-867b-5ea61fbbf120") }}
					</v-btn>

					<v-btn
						v-if="isAnsbachDomain"
						variant="flat"
						block
						:color="workspacesStore.isOpenedWorkspaceLocked ? 'success' : 'error'"
						class="mb-2"
						:disabled="workspacesStore.isOpenedWorkspaceLocked"
						@click="clickFinishNotruf"
					>
						{{ workspacesStore.isOpenedWorkspaceLocked ? "Notruf abgeschlossen" : "Notruf abschließen" }}
					</v-btn>

					<!-- pinned questions -->
					<transition-group name="questionlist">
						<question-card
							v-for="item in questionsStore.pinnedQuestions"
							:key="item.instanceTask.id"
							:title="item.title[domainStore.languagePreference]"
							:subtitle="item.subtitle[domainStore.languagePreference]"
							:instance-task="item.instanceTask"
							:show-unknown-button="item.showUnknownButton"
							:play-button="item.playButton"
							class="mb-2 pb-3"
						/>
					</transition-group>

					<!-- meta questions && hint notification -->
					<v-card v-if="hint || !!questionsStore.metaQuestion" class="mb-2" rounded="lg" elevation="2">
						<div>
							<hint-notification v-if="hint" class="mb-2" />
							<v-expansion-panels
								v-if="!!questionsStore.metaQuestion"
								v-model="metaQuestionPanel"
								static
								mandatory
								hide-actions
							>
								<question-panel
									:key="questionsStore.metaQuestion.instanceTask.id"
									:title="questionsStore.metaQuestion.title[domainStore.languagePreference]"
									:subtitle="questionsStore.metaQuestion.subtitle[domainStore.languagePreference]"
									:noGPTRephrasing="questionsStore.metaQuestion.noGPTRephrasing"
									:playButton="questionsStore.metaQuestion.playButton"
									:showUnknownButton="questionsStore.metaQuestion.showUnknownButton"
									:instance-task="questionsStore.metaQuestion.instanceTask"
									:index="0"
								/>
							</v-expansion-panels>
						</div>
					</v-card>

					<!-- open closed question tabs -->
					<div class="d-flex align-center">
						<v-btn-toggle
							v-model="questionsStore.tab"
							rounded="md"
							style="flex: 1"
							mandatory
							class="d-flex tab-toggle"
							density="compact"
						>
							<v-btn size="small" density="compact" style="width: 50%" value="open" class="tab-btn">
								<div class="mr-1">
									{{ symbolsStore.GUILabel("11c347b2-c087-48f6-979c-7d39ab30ad53") }}
								</div>
								<v-chip v-if="questionsStore.unansweredQuestions.length > 0" density="comfortable">
									{{ questionsStore.unansweredQuestions.length }}
								</v-chip>
							</v-btn>
							<v-btn size="small" density="compact" style="width: 50%" value="closed" class="tab-btn">
								<div class="mr-1">
									{{ symbolsStore.GUILabel("fcd4e672-91cf-459c-bd59-aa31103bfdac") }}
								</div>
								<v-chip v-if="questionsStore.answeredQuestions.length > 0" density="comfortable">
									{{ questionsStore.answeredQuestions.length }}
								</v-chip>
							</v-btn>
						</v-btn-toggle>

						<!-- undo button -->
						<v-tooltip v-if="showUndo" location="bottom" :open-delay="500">
							<template v-slot:activator="{ props }">
								<v-btn
									v-bind="props"
									icon
									size="small"
									:disabled="!questionHistoryStore.canUndo"
									@click="questionHistoryStore.undo()"
									class="ml-1"
								>
									<v-icon>mdi-undo</v-icon>
								</v-btn>
							</template>
							<span>{{ symbolsStore.GUILabel("e7699fb6-6e04-491c-aee6-d1dc0b587374") }}</span>
						</v-tooltip>
					</div>
				</div>

				<!-- non-pinned open and closed questions -->
				<div class="questions-scroll-wrapper" :style="{ '--scrollbar-width': scrollbarWidth + 'px' }">
					<div v-show="canScrollUp" class="scroll-fade scroll-fade-top"></div>
					<div v-show="canScrollDown" class="scroll-fade scroll-fade-bottom"></div>
					<div ref="scrollContainer" class="questions-scroll-container" @scroll="updateScrollState">
						<v-window v-model="questionsStore.tab">
							<v-window-item value="open" :class="{ 'beta-case-exists': !!betaCaseId }">
								<div v-if="questionsStore.tab === 'open'">
									<dataobject-questions
										v-for="(dataobject, index) in questionDataobjects"
										:key="dataobject.id"
										:dataobject="dataobject"
										:dataobjects-in-workspace-count="questionDataobjects.length"
										:concept-count="dataobjectCounts[dataobject.id]?.count || 1"
										:concept-index="dataobjectCounts[dataobject.id]?.index || 1"
										:auto-expand="true"
										:class="{ 'mb-2': index === questionDataobjects.length - 1 }"
									/>
								</div>
							</v-window-item>
							<v-window-item value="closed" :class="{ 'beta-case-exists': !!betaCaseId }">
								<div v-if="questionsStore.tab === 'closed'">
									<v-expansion-panels
										v-model="expandedClosedQuestions"
										multiple
										class="px-2 compact-panels"
									>
										<question-panel
											v-for="(item, i) in sortedClosedQuestions"
											:key="item.instanceTask.id"
											:title="item.title[domainStore.languagePreference]"
											:subtitle="item.subtitle[domainStore.languagePreference]"
											:noGPTRephrasing="item.noGPTRephrasing"
											:playButton="item.playButton"
											:showUnknownButton="item.showUnknownButton"
											:instance-task="item.instanceTask"
											:index="i"
											class="my-2"
											style="margin-top: 0 !important"
										/>
									</v-expansion-panels>
								</div>
							</v-window-item>
						</v-window>
					</div>
				</div>

				<div
					style="position: absolute; bottom: 5px; right: 0"
					:style="{ width: sidebarWidth + 'px' }"
					class="d-flex justify-space-between px-5"
				>
					<logs-button
						v-if="domainStore.isEditorOrAdmin && (debugStore.altKeyHeld || debugStore.debugMode)"
						size="x-small"
					/>
					<p style="color: grey">
						Thoughts: {{ graphStore.metadata.totalThoughts }} • {{ graphStore.metadata.cycleThoughts }} ({{
							graphStore.metadata.computationTime
						}}ms)
					</p>
					<p
						style="color: grey"
						v-if="domainStore.isEditorOrAdmin && (debugStore.altKeyHeld || debugStore.debugMode)"
					>
						Version: {{ graphStore.graph.version }}
					</p>
				</div>
			</div>
		</template>
	</v-card>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useGraphStore } from "../../../store/graph";
import { useQuestionsStore } from "../../../store/questions";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useSessionStore } from "../../../store/alternis/session";
import { useSymbolsStore } from "../../../store/symbols";
import { useDebugStore } from "../../../store/debug";
import { useQuestionHistoryStore } from "../../../store/questionHistory";
import QuestionCard from "./QuestionCard.vue";
import HintNotification from "./HintNotification.vue";
import LogsButton from "./LogsButton.vue";
import router from "../../../router";
import QuestionPanel from "./QuestionPanel.vue";
import DataobjectQuestions from "./DataobjectQuestions.vue";
import { InstanceDataobject, InstanceTask } from "@lr-notitia/notitia-operator/schemas";
import { useDomainStore } from "../../../store/domain";
import { useInstanceDataspacesStore } from "../../../store/i-dataspace";
import { useResponsiveLayout } from "@lr-notitia/notitia-ui";

const graphStore = useGraphStore();
const questionsStore = useQuestionsStore();
const workspacesStore = useInstanceWorkspacesStore();
const sessionStore = useSessionStore();
const domainStore = useDomainStore();
const symbolsStore = useSymbolsStore();
const debugStore = useDebugStore();
const questionHistoryStore = useQuestionHistoryStore();
const instanceDataspacesStore = useInstanceDataspacesStore();

const { displayResponsive } = useResponsiveLayout();

const MIN_SIDEBAR_WIDTH = 420;

function getStoredWidth(): number {
	const stored = localStorage.getItem("widthQuestions");
	if (stored) {
		const parsed = Number(stored);
		if (!isNaN(parsed) && parsed >= MIN_SIDEBAR_WIDTH) return parsed;
	}
	return MIN_SIDEBAR_WIDTH;
}

const sidebarWidth = ref(getStoredWidth());
const isAnsbachDomain = computed(() => domainStore.domain.id === "e5073400-a6b4-455f-abdd-f410e729945c");

function updateCssVariable(width: number) {
	document.documentElement.style.setProperty("--questions-sidebar-width", width + "px");
}

watch(sidebarWidth, (w) => updateCssVariable(w), { immediate: true });

function setResizeEvents() {
	const el = document.getElementById("questionsborder");
	let isDragged = false;
	if (!el) return;

	function setWidth(width: number) {
		const maxWidth = window.innerWidth * 0.4;
		if (width < MIN_SIDEBAR_WIDTH) {
			width = MIN_SIDEBAR_WIDTH;
		} else if (width > maxWidth) {
			width = maxWidth;
		}
		sidebarWidth.value = width;
		localStorage.setItem("widthQuestions", String(width));
	}

	function resize(e: PointerEvent) {
		e.preventDefault();
		e.stopPropagation();
		document.body.style.cursor = "ew-resize";
		const width = window.innerWidth - e.clientX;
		setWidth(width);
	}

	el.addEventListener(
		"pointerdown",
		function (e: PointerEvent) {
			isDragged = true;
			el.style.transition = "initial";
			document.addEventListener("pointermove", resize, false);
		},
		false,
	);
	document.addEventListener(
		"pointerup",
		function (e: PointerEvent) {
			if (!isDragged) return;
			el.style.transition = "";
			const width = window.innerWidth - e.clientX;
			setWidth(width);
			document.body.style.cursor = "";
			document.removeEventListener("pointermove", resize, false);
			isDragged = false;
		},
		false,
	);
}

const showUndo = computed(() => instanceDataspacesStore.environmentVariables["SHOW_UNDO_BUTTON"] === "true");

let betaCaseId = router.currentRoute.value.params.betaCaseId as string;

const expandedClosedQuestions = ref<string[]>([]);
const scrollContainer = ref<HTMLElement | null>(null);
const canScrollUp = ref(false);
const canScrollDown = ref(false);
const scrollbarWidth = ref(0);

function updateScrollState() {
	if (!scrollContainer.value) return;
	const { scrollTop, scrollHeight, clientHeight, offsetWidth } = scrollContainer.value;
	canScrollUp.value = scrollTop > 0;
	canScrollDown.value = scrollTop + clientHeight < scrollHeight - 1;
	scrollbarWidth.value = offsetWidth - scrollContainer.value.clientWidth;
}

const sortedClosedQuestions = computed(() => {
	const items = [...questionsStore.answeredQuestions];
	return items.sort((a, b) => {
		const getTimestamp = (task: InstanceTask) => {
			const ab = task.context?.answeredBy;
			if (!ab) return 0;
			let max = 0;
			Object.values(ab).forEach((val: any) => {
				if (Array.isArray(val)) {
					val.forEach((entry) => {
						if (entry?.timestamp && entry.timestamp > max) max = entry.timestamp;
					});
				}
			});
			return max;
		};
		return getTimestamp(b.instanceTask) - getTimestamp(a.instanceTask);
	});
});

watch(
	() => sortedClosedQuestions.value.map((q) => q.instanceTask.id).join(","),
	() => {
		expandedClosedQuestions.value = sortedClosedQuestions.value.map((q) => q.instanceTask.id);
	},
	{ immediate: true },
);

// Update scroll state when questions change
watch(
	() => [questionsStore.unansweredQuestions.length, questionsStore.answeredQuestions.length, questionsStore.tab],
	() => {
		nextTick(() => updateScrollState());
	},
);

const hint = computed(() => graphStore.descriptor.notifications.find((n) => n.display === "hint"));

const metaQuestionPanel = computed(() => {
	return questionsStore.metaQuestion ? [questionsStore.metaQuestion.instanceTask.id] : [];
});
const questionDataobjects = computed(() => {
	let alreadyVisited = new Set<string>();
	function children(id: string): InstanceDataobject[] {
		if (alreadyVisited.has(id)) return [];
		alreadyVisited.add(id);
		let d = graphStore.instance.dataobject.byId(id);
		if (!d) return [];
		let c = d.hasA.map((hasAId: string) => graphStore.instance.dataobject.byId(hasAId)) as InstanceDataobject[];
		c.forEach((hasADataobject) => {
			if (!hasADataobject.template) return;
			c = c.concat(children(hasADataobject.id));
		});
		return c;
	}
	let dataobjects: InstanceDataobject[] = workspacesStore.openedWorkspace?.dataobjects
		.map((id) => graphStore.instance.dataobject.byId(id))
		.filter((d) => d) as InstanceDataobject[];
	dataobjects.forEach((dataobject) => {
		dataobjects = dataobjects.concat(children(dataobject.id));
	});

	const uniqueDataobjects = Array.from(new Map(dataobjects.map((d) => [d.id, d])).values());

	const sorted = uniqueDataobjects.sort((a, b) => {
		if (a.concept === b.concept) return 0;
		if (a.concept < b.concept) return -1;
		return 1;
	});

	return sorted.sort((a, b) => {
		if (a.id === workspacesStore.selectedNodeId) return -1;
		if (b.id === workspacesStore.selectedNodeId) return 1;
		return 0;
	});
});

const dataobjectCounts = computed(() => {
	const conceptCounts: Record<string, number> = {};
	const conceptIndexes: Record<string, number> = {};
	const counts: Record<string, { count: number; index: number }> = {};

	// Count occurrences of each concept
	questionDataobjects.value.forEach((dataobject) => {
		conceptCounts[dataobject.concept] = (conceptCounts[dataobject.concept] || 0) + 1;
	});

	// Add count property to each dataobject
	questionDataobjects.value.forEach((dataobject) => {
		conceptIndexes[dataobject.concept] = (conceptIndexes[dataobject.concept] || 0) + 1;
		counts[dataobject.id] = {
			count: conceptCounts[dataobject.concept],
			index: conceptIndexes[dataobject.concept],
		};
	});

	return counts;
});

function clickFinishBeta() {
	const protocol = window.location.protocol === "http:" ? "http" : "https";
	const isProduction = protocol === "https";
	const route = `/beta/${betaCaseId}/survey`;
	const alternisMicrophoneUsed = sessionStore.alternisMicrophoneUsed ? "?alternisMicrophoneUsed=true" : "";
	if (isProduction) {
		const url = "https://kinas.notitia.world" + route + alternisMicrophoneUsed;
		window.location.href = url;
	} else {
		const url = "http://localhost:3001" + route + alternisMicrophoneUsed;
		window.location.href = url;
	}
}

async function clickFinishNotruf() {
	const workspaceId = workspacesStore.openedWorkspaceId;
	if (!workspaceId) return;
	if (workspacesStore.isWorkspaceLocked(workspaceId)) return;
	const confirmed = window.confirm(
		"Notruf wirklich abschließen? Der Workspace wird dadurch schreibgeschützt und kann nicht mehr bearbeitet werden. Sie werden anschließend zur Startseite weitergeleitet.",
	);
	if (!confirmed) return;
	await workspacesStore.lockWorkspace(workspaceId);
	router.push("/ils-ansbach");
}

watch(
	() => questionsStore.tab,
	() => {
		questionsStore.currentQuestion = undefined;
	},
);

function handleUndo(e: KeyboardEvent) {
	if ((e.metaKey || e.ctrlKey) && e.key === "z" && showUndo.value && questionHistoryStore.canUndo) {
		const tag = (e.target as HTMLElement)?.tagName;
		if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
		e.preventDefault();
		questionHistoryStore.undo();
	}
}

onMounted(() => {
	questionHistoryStore.clear();
	questionsStore.initSSE();
	nextTick(() => {
		updateScrollState();
		setResizeEvents();
	});
	window.addEventListener("keydown", handleUndo);
});
onUnmounted(() => {
	window.removeEventListener("keydown", handleUndo);
	questionsStore.closeSSE();
	questionHistoryStore.clear();
});
</script>

<style scoped lang="scss">
#questionsborder {
	position: absolute;
	left: 0;
	top: 0;
	width: 2px;
	height: 100%;
	z-index: 999;
	cursor: ew-resize;
	user-select: none;
}

.questions-sidebar-card :deep(.v-card-text) {
	padding: 16px 16px 0 16px;
}

.questionlist-enter-active,
.questionlist-leave-active {
	transition: all 0.5s ease;
}
.questionlist-enter-from,
.questionlist-leave-to {
	opacity: 0;
	transform: translateX(30px);
}

.tab-toggle {
	:deep(.tab-btn) {
		opacity: 0.5;
		transition: all 0.2s ease;
		border-bottom: 2px solid transparent;

		&.v-btn--active {
			opacity: 1;
			border-bottom: 2px solid #a7a7a7;
		}
	}
}

.compact-panels :deep(.v-expansion-panel-title) {
	min-height: 48px !important;
	padding: 12px 16px 12px 16px !important;
}

.questions-scroll-wrapper {
	position: relative;
	flex: 1;
	overflow: hidden;
	margin-top: 8px;
}

.scroll-fade {
	position: absolute;
	left: 0;
	right: var(--scrollbar-width, 0px);
	height: 32px;
	pointer-events: none;
	z-index: 10;
}

.scroll-fade-top {
	top: 0;
	background: linear-gradient(
		to bottom,
		rgb(var(--v-theme-contained)) 0%,
		rgb(var(--v-theme-contained)) 5%,
		transparent 100%
	);
}

.scroll-fade-bottom {
	bottom: 0;
	background: linear-gradient(
		to top,
		rgb(var(--v-theme-contained)) 0%,
		rgb(var(--v-theme-contained)) 5%,
		transparent 100%
	);
}

.questions-scroll-container {
	height: 100%;
	overflow-y: auto;
}
</style>
